import type {
  LockerverseAuction,
  LockerverseAuctionItem,
  LockerverseAuctionStatus,
} from "@lockerverse/sdk/auctions";
import { formatMoney } from "./format-money.ts";

// Match the public web app. Keep display policy here for all SDK auction surfaces.
export function formatAuctionBoundary(
  auction: LockerverseAuction,
  status: LockerverseAuctionStatus = auction.status,
  item?: LockerverseAuctionItem
) {
  const starts = status === "draft";
  const nextItem = item ?? getNextAuctionItem(auction);
  const value = starts
    ? (nextItem?.startAt ?? auction.startAt)
    : ((status === "finished" ? item?.finishedAt : undefined) ??
      item?.endAt ??
      auction.endAt);
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) {
    return "";
  }
  const explicitBoundary = starts ? nextItem?.startAt : item?.endAt;
  const showTime = explicitBoundary
    ? true
    : ((starts
        ? auction.configuration.startTimeEnabled
        : auction.configuration.endTimeEnabled) ?? true);
  const dateLabel = new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "short",
    timeZone: "America/New_York",
    year: "numeric",
  }).format(date);
  if (!showTime) {
    return dateLabel;
  }
  const timeLabel = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "America/New_York",
    timeZoneName: "short",
  }).format(date);
  return `${dateLabel} at ${timeLabel}`;
}

export function formatAuctionMoney(cents: number) {
  return formatMoney(cents, 0);
}

export function getAuctionDisplayStatus(
  auction: LockerverseAuction,
  item?: LockerverseAuctionItem
): LockerverseAuctionStatus {
  if (auction.status === "finished" || item?.status === "finished") {
    return "finished";
  }
  // Preserve legacy draft responses until both item boundaries are available.
  if (
    item &&
    !(item.startAt && item.endAt) &&
    (item.status === "draft" || auction.status === "draft")
  ) {
    return "draft";
  }
  const now = Date.now();
  if (Date.parse(item?.endAt ?? auction.endAt) <= now) {
    return "finished";
  }
  if (Date.parse(item?.startAt ?? auction.startAt) > now) {
    return "draft";
  }
  if (item) {
    return "active";
  }
  if (auction.items.length) {
    const statuses = auction.items.map((entry) =>
      getAuctionDisplayStatus(auction, entry)
    );
    if (statuses.includes("active")) {
      return "active";
    }
    return statuses.includes("draft") ? "draft" : "finished";
  }
  return auction.status;
}

/** The next scheduled item, including a gap between groups of items. */
export function getNextAuctionItem(auction: LockerverseAuction) {
  const now = Date.now();
  return auction.items
    .filter(
      (item) =>
        item.status !== "finished" &&
        Date.parse(item.startAt ?? auction.startAt) > now
    )
    .sort(
      (left, right) =>
        Date.parse(left.startAt ?? auction.startAt) -
        Date.parse(right.startAt ?? auction.startAt)
    )[0];
}

export function isAuctionItemSoldOut(item: LockerverseAuctionItem) {
  return (
    item.isSoldOut ||
    (item.saleMode === "buy_now" && item.availableQuantity === 0)
  );
}
